/**
 * タスク分析の集計。
 *
 * 画面から計算を切り離しておく（数字が合わないときに、ここだけ見れば済むようにする）。
 *
 * 時間は「想定時間」を使う。実績時間は現行のシートでも空のことが多く、いまのタスクは
 * 月次経理（FMT）から起こしたものしか時間を持たないため、件数と時間のどちらでも
 * 見られるようにしてある。
 *
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business タスクを担当者別・種別別に集計し、合計時間・件数・完了率を出す。
 * @graph-connects none
 */
import type { Task } from './task.js';

/** 集計の単位。件数で見るか、想定時間で見るか */
export type Metric = 'count' | 'hours';

/** 種別ごとの集計 */
export interface KindTotal {
  /** 種別 */
  kind: string;
  /** 件数 */
  count: number;
  /** 想定時間の合計 */
  hours: number;
}

/** 担当者ごとの集計 */
export interface PersonTotal {
  /** 担当者 */
  assignee: string;
  /** 件数 */
  count: number;
  /** 対応済の件数 */
  doneCount: number;
  /** 完了率（0〜100） */
  completionRate: number;
  /** 想定時間の合計 */
  hours: number;
  /** 種別ごとの内訳。積み上げバーの並び順に使う */
  segments: KindTotal[];
  /** そのタスク一覧（件数の多い順に並べたもの） */
  tasks: Task[];
}

/** 全体の集計 */
export interface Summary {
  /** 想定時間の合計 */
  totalHours: number;
  /** 対応済のぶんの想定時間 */
  doneHours: number;
  /** 件数 */
  count: number;
  /** 対応済の件数 */
  doneCount: number;
  /** 担当者の人数 */
  memberCount: number;
  /** 一人あたりの想定時間 */
  hoursPerPerson: number;
  /** 完了率（0〜100） */
  completionRate: number;
}

/** @graph-connects none */
function hoursOf(task: Task): number {
  return task.estimatedHours ?? 0;
}

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 全体の合計・完了率・一人あたりの時間を出す。担当者が 0 人のときは 0 を返す（割り算を避ける）
 * @graph-connects none
 */
export function summarize(tasks: readonly Task[]): Summary {
  const done = tasks.filter((task) => task.status === '対応済');
  const memberCount = new Set(tasks.map((task) => task.assignee)).size;
  const totalHours = tasks.reduce((sum, task) => sum + hoursOf(task), 0);
  return {
    totalHours,
    doneHours: done.reduce((sum, task) => sum + hoursOf(task), 0),
    count: tasks.length,
    doneCount: done.length,
    memberCount,
    hoursPerPerson: memberCount === 0 ? 0 : totalHours / memberCount,
    completionRate: tasks.length === 0 ? 0 : (done.length / tasks.length) * 100,
  };
}

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 種別ごとに件数と想定時間を集計する。指定の単位で多い順に並べる
 * @graph-connects none
 */
export function totalsByKind(tasks: readonly Task[], metric: Metric = 'count'): KindTotal[] {
  const byKind = new Map<string, KindTotal>();
  for (const task of tasks) {
    const total = byKind.get(task.kind) ?? { kind: task.kind, count: 0, hours: 0 };
    total.count += 1;
    total.hours += hoursOf(task);
    byKind.set(task.kind, total);
  }
  return [...byKind.values()].sort(
    (a, b) => b[metric] - a[metric] || a.kind.localeCompare(b.kind, 'ja'),
  );
}

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 担当者ごとに、種別の内訳つきで集計する。積み上げバーと担当者別カードの両方に使う
 * @graph-connects totalsByKind [calls] 担当者ごとの種別内訳を作る
 */
export function totalsByPerson(tasks: readonly Task[], metric: Metric = 'count'): PersonTotal[] {
  const byPerson = new Map<string, Task[]>();
  for (const task of tasks) {
    const same = byPerson.get(task.assignee);
    if (same === undefined) {
      byPerson.set(task.assignee, [task]);
    } else {
      same.push(task);
    }
  }
  return [...byPerson.entries()]
    .map(([assignee, owned]) => ({
      assignee,
      count: owned.length,
      doneCount: owned.filter((task) => task.status === '対応済').length,
      completionRate:
        owned.length === 0
          ? 0
          : (owned.filter((task) => task.status === '対応済').length / owned.length) * 100,
      hours: owned.reduce((sum, task) => sum + hoursOf(task), 0),
      segments: totalsByKind(owned, metric),
      // カードに出す並び。時間の大きいもの、同じなら期日の早いものを上に
      tasks: [...owned].sort(
        (a, b) => hoursOf(b) - hoursOf(a) || a.dueDate.localeCompare(b.dueDate),
      ),
    }))
    .sort((a, b) => b[metric] - a[metric] || a.assignee.localeCompare(b.assignee, 'ja'));
}

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 集計値を表示用の文字列にする。時間は小数 1 桁、件数はそのまま
 * @graph-connects none
 */
export function formatMetric(value: number, metric: Metric): string {
  return metric === 'hours' ? `${value.toFixed(1)}h` : `${String(value)}件`;
}
